import { useState } from 'react';
import Header from '../../components/base/Header';
import Footer from '../../components/base/Footer';
import { Link } from 'react-router-dom';

interface SunTimeData {
  sunrise: string;
  sunset: string;
  dayLength: string;
  location: string;
}

export default function Home() {
  const [locationInput, setLocationInput] = useState('');
  const [sunTimeData, setSunTimeData] = useState<SunTimeData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSunTimes = async () => {
    if (!locationInput.trim()) {
      setError('場所を入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Geocoding API で場所の緯度経度を取得
      const geoResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationInput)}&format=json&limit=1&accept-language=ja`
      );
      const geoData = await geoResponse.json();

      if (!geoData || geoData.length === 0) {
        setError('場所が見つかりませんでした。別の場所名を試してください。');
        setIsLoading(false);
        return;
      }

      const { lat, lon, display_name } = geoData[0];

      // Sunrise-Sunset API で日の出・日の入り時刻を取得
      const sunResponse = await fetch(
        `https://api.sunrise-sunset.org/json?lat=${lat}&lng=${lon}&formatted=0&date=today`
      );
      const sunData = await sunResponse.json();

      if (sunData.status === 'OK') {
        const sunrise = new Date(sunData.results.sunrise);
        const sunset = new Date(sunData.results.sunset);
        
        // 日本時間に変換
        const sunriseJST = new Date(sunrise.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
        const sunsetJST = new Date(sunset.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));

        // 日照時間を計算
        const dayLengthMs = sunsetJST.getTime() - sunriseJST.getTime();
        const hours = Math.floor(dayLengthMs / (1000 * 60 * 60));
        const minutes = Math.floor((dayLengthMs % (1000 * 60 * 60)) / (1000 * 60));

        setSunTimeData({
          sunrise: sunriseJST.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }),
          sunset: sunsetJST.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }),
          dayLength: `${hours}時間${minutes}分`,
          location: display_name
        });
      } else {
        setError('日照時間の取得に失敗しました。');
      }
    } catch (err) {
      setError('データの取得中にエラーが発生しました。');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      fetchSunTimes();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('https://public.readdy.ai/ai/img_res/a9bdab61a9a78f0a8fa20c853b20d71c.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              花と植物の
              <span className="text-pink-300">美しい世界</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              胡蝶蘭、シクラメン、チランジアをはじめとする植物の魅力を発見し、
              <br />
              あなたの緑のある暮らしを豊かにしませんか
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/plants"
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                植物図鑑を見る
              </Link>
              <Link 
                to="/essay"
                className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                エッセイを読む
              </Link>
              <Link 
                to="/today-flower"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                今日の誕生花を探す
              </Link>
              <Link 
                to="/omikuji"
                className="bg-pink-500 hover:bg-pink-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                花のおみくじ
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* February Flowers Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-50 via-white to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-4">2月の花</h2>
            <p className="text-lg text-gray-600">早春を彩る美しい花々をご紹介</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Flower 1 */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="h-64 w-full overflow-hidden bg-white">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20pink%20plum%20blossom%20ume%20flowers%20blooming%20in%20early%20spring%20with%20delicate%20petals%20on%20branches%2C%20elegant%20Japanese%20botanical%20photography%2C%20soft%20natural%20lighting%2C%20simple%20clean%20white%20background%2C%20winter%20to%20spring%20transition%20flower%2C%20detailed%20close-up%20view%20showing%20graceful%20blooms&width=400&height=300&seq=february-plum-blossom&orientation=landscape"
                  alt="梅"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-pink-600 mb-3">梅（ウメ）</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  早春を代表する花。寒さの中でも凛と咲く姿が美しく、春の訪れを告げる花として古くから愛されています。
                </p>
                <div className="flex items-center text-sm text-gray-500">
                  <i className="ri-heart-line mr-2"></i>
                  <span>花言葉：高潔、忍耐</span>
                </div>
              </div>
            </div>

            {/* Flower 2 */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20white%20snowdrop%20flowers%20blooming%20in%20snow%20with%20delicate%20drooping%20bell-shaped%20petals%2C%20graceful%20botanical%20photography%2C%20soft%20natural%20lighting%2C%20simple%20clean%20white%20background%2C%20early%20spring%20flower%2C%20detailed%20close-up%20view%20showing%20pure%20white%20blooms&width=400&height=300&seq=february-snowdrop&orientation=landscape"
                  alt="スノードロップ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-blue-600 mb-3">スノードロップ</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  雪の中でも咲く可憐な白い花。下向きに咲く姿が愛らしく、春の希望を象徴する花として人気があります。
                </p>
                <div className="flex items-center text-sm text-gray-500">
                  <i className="ri-heart-line mr-2"></i>
                  <span>花言葉：希望、慰め</span>
                </div>
              </div>
            </div>

            {/* Flower 3 */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20yellow%20wintersweet%20chimonanthus%20praecox%20flowers%20blooming%20in%20winter%20with%20waxy%20translucent%20yellow%20petals%20and%20sweet%20fragrance%2C%20elegant%20botanical%20photography%2C%20soft%20natural%20lighting%2C%20simple%20clean%20white%20background%2C%20winter%20fragrant%20flower%2C%20detailed%20close-up%20view%20showing%20delicate%20blooms&width=400&height=300&seq=february-wintersweet&orientation=landscape"
                  alt="ロウバイ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-amber-600 mb-3">ロウバイ（蝋梅）</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  冬の寒さの中で甘い香りを放つ黄色い花。蝋細工のような質感が美しく、心を温めてくれます。
                </p>
                <div className="flex items-center text-sm text-gray-500">
                  <i className="ri-heart-line mr-2"></i>
                  <span>花言葉：慈愛、先見</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Character Section */}
      <section className="py-16 bg-gradient-to-br from-pink-50 to-green-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-green-800 mb-8">サイトキャラクター紹介</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-orange-100 to-pink-100 rounded-full flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/b6492b1afc5b3c4e8edd789ad2bb0153.png"
                  alt="蘭ちゃん"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="text-2xl font-bold text-orange-600 mb-4">蘭ちゃん</h3>
              <p className="text-gray-600 leading-relaxed">
                蘭々とシッくんの優しい飼い主。
                植物と動物を愛する心優しい女性で、
                「みんなの植物～モモちゃん畑～」の管理人として、
                花と植物の魅力を伝えながら、
                蘭々とシッくんと一緒に楽しい毎日を過ごしています。
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-pink-200 to-purple-200 rounded-full flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/ba224d2fc6ab46183f0c8a9298ee61fe.png"
                  alt="蘭々"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="text-2xl font-bold text-pink-600 mb-4">蘭々</h3>
              <p className="text-gray-600 leading-relaxed">
                胡蝶蘭をモチーフにした優雅で上品なキャラクター。
                花言葉は「幸福が飛んでくる」「純粋な愛」。
                みんなに幸せを運んでくれる素敵な仲間です。
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-red-200 to-pink-200 rounded-full flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/8dff19b5520e46f561312bc594d27f43.png"
                  alt="シッくん"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="text-2xl font-bold text-red-500 mb-4">シッくん</h3>
              <p className="text-gray-600 leading-relaxed">
                シクラメンをモチーフにしたポメラニアンの元気で明るいキャラクター。
                花言葉は「遠慮」「気後れ」「内気」。
                でも実はとても温かい心を持った頼もしい仲間です。
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-red-300 to-rose-300 rounded-full flex items-center justify-center overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8ca6952e6a33257e04fb8ac67bcd75fe/064aec71169f8a79c28dee9a7a0591eb.png"
                  alt="ローズくん"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="text-2xl font-bold text-red-600 mb-4">ローズくん</h3>
              <p className="text-gray-600 leading-relaxed">
                蘭ちゃんのお友達。ローズくんの心には、
                赤いバラのように燃える 情熱 と、
                そっと寄り添う 愛情 が宿っています。
                楽しさにはいつだって全力で、
                困っている誰かには迷いなく手を伸ばす。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Character Goods Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-400 via-cyan-400 to-emerald-400 rounded-full mb-6 shadow-lg">
                <i className="ri-shopping-bag-3-line text-white text-4xl"></i>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-teal-800 mb-4">
                キャラクターグッズ販売中
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                蘭ちゃん、蘭々、シッくん、ローズくんのオリジナルグッズをSUZURIで販売しています
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="h-80 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Cute%20kawaii%20character%20merchandise%20display%20with%20colorful%20t-shirts%20mugs%20and%20stickers%20featuring%20adorable%20flower-themed%20animal%20characters%2C%20cheerful%20product%20photography%2C%20bright%20natural%20lighting%2C%20simple%20clean%20white%20background%2C%20online%20shop%20theme%20with%20playful%20arrangement&width=600&height=400&seq=character-goods&orientation=landscape"
                    alt="キャラクターグッズ"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="mb-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center">
                        <i className="ri-t-shirt-line text-teal-600 text-xl"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800">Tシャツ・アパレル</h3>
                    </div>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-rose-100 rounded-full flex items-center justify-center">
                        <i className="ri-cup-line text-pink-600 text-xl"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800">マグカップ・雑貨</h3>
                    </div>
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-green-100 rounded-full flex items-center justify-center">
                        <i className="ri-sticky-note-line text-emerald-600 text-xl"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800">ステッカー・文具</h3>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    かわいいキャラクターたちがデザインされたオリジナルグッズを多数ご用意しています。
                    日常使いにぴったりなアイテムで、あなたの毎日を彩りませんか？
                  </p>
                  <div className="text-center">
                    <a
                      href="https://suzuri.jp/morikawa_hobby/products"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500 hover:from-teal-600 hover:via-cyan-600 hover:to-emerald-600 text-white font-bold py-4 px-10 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-external-link-line mr-2 text-xl"></i>
                      グッズショップを見る
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                <i className="ri-information-line mr-1"></i>
                SUZURIの外部サイトに移動します
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sun Time Section - NEW */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-400 via-orange-400 to-yellow-400 rounded-full mb-6 shadow-lg">
                <i className="ri-sun-line text-white text-4xl"></i>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-orange-800 mb-4">
                今日の日照時間
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                植物の育成に重要な日照時間を確認しましょう
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden p-8 md:p-12">
              {/* 検索フォーム */}
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-800 mb-4">
                  <i className="ri-map-pin-line mr-2 text-orange-600"></i>
                  場所を入力してください
                </label>
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="例: 東京都、大阪市、札幌"
                    className="flex-1 px-6 py-4 border-2 border-gray-200 rounded-full focus:border-orange-400 focus:outline-none transition-colors text-base"
                  />
                  <button
                    onClick={fetchSunTimes}
                    disabled={isLoading}
                    className="whitespace-nowrap px-8 py-4 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 hover:from-amber-600 hover:via-orange-600 hover:to-yellow-600 text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        検索中...
                      </>
                    ) : (
                      <>
                        <i className="ri-search-line mr-2"></i>
                        日照時間を調べる
                      </>
                    )}
                  </button>
                </div>
                {error && (
                  <p className="mt-4 text-red-600 text-sm flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    {error}
                  </p>
                )}
              </div>

              {/* 結果表示 */}
              {sunTimeData && (
                <div className="mt-8 animate-fadeIn">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-orange-200">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">
                        <i className="ri-map-pin-2-fill text-orange-600 mr-2"></i>
                        {sunTimeData.location}
                      </h3>
                      <p className="text-gray-600">
                        {new Date().toLocaleDateString('ja-JP', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          weekday: 'long'
                        })}
                      </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      {/* 日の出 */}
                      <div className="bg-white rounded-xl p-6 shadow-md text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-sun-line text-3xl text-orange-600"></i>
                        </div>
                        <h4 className="text-lg font-bold text-gray-800 mb-2">日の出</h4>
                        <p className="text-3xl font-bold text-orange-600">{sunTimeData.sunrise}</p>
                      </div>

                      {/* 日照時間 */}
                      <div className="bg-white rounded-xl p-6 shadow-md text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-yellow-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-time-line text-3xl text-yellow-600"></i>
                        </div>
                        <h4 className="text-lg font-bold text-gray-800 mb-2">日照時間</h4>
                        <p className="text-3xl font-bold text-yellow-600">{sunTimeData.dayLength}</p>
                      </div>

                      {/* 日の入り */}
                      <div className="bg-white rounded-xl p-6 shadow-md text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-moon-line text-3xl text-red-600"></i>
                        </div>
                        <h4 className="text-lg font-bold text-gray-800 mb-2">日の入り</h4>
                        <p className="text-3xl font-bold text-red-600">{sunTimeData.sunset}</p>
                      </div>
                    </div>

                    {/* アドバイス */}
                    <div className="mt-8 bg-white rounded-xl p-6 shadow-md">
                      <h4 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                        <i className="ri-lightbulb-line text-green-600 mr-2"></i>
                        植物育成のヒント
                      </h4>
                      <p className="text-gray-700 leading-relaxed">
                        日照時間は植物の成長に大きく影響します。多くの植物は1日6時間以上の日光を必要とします。
                        冬季は日照時間が短くなるため、室内の明るい場所に置いたり、必要に応じて植物育成ライトの使用を検討しましょう。
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 初期状態の説明 */}
              {!sunTimeData && !isLoading && !error && (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">☀️</div>
                  <p className="text-gray-600 leading-relaxed">
                    場所を入力して、その地域の今日の日の出・日の入り時刻と<br />
                    日照時間を確認できます。植物の育成計画にお役立てください。
                  </p>
                </div>
              )}
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600">
                <i className="ri-information-line mr-1"></i>
                データは OpenStreetMap と Sunrise-Sunset API から取得しています
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Plant Doctor App Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 via-emerald-400 to-teal-400 rounded-full mb-6 shadow-lg">
                <i className="ri-plant-line text-white text-4xl"></i>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-4">
                ももちゃん植物ドクター
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                AI植物診断アプリで、あなたの植物ライフをもっと楽しく！
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8">
              <div className="p-8 md:p-12">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-rose-100 rounded-full flex items-center justify-center">
                    <i className="ri-smartphone-line text-pink-600 text-xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800">アプリ概要</h3>
                </div>
                <p className="text-gray-700 leading-relaxed mb-8">
                  ももちゃん植物ドクターは、植物の育成を楽しくサポートする可愛いキャラクター付きWebアプリです。写真診断、花・野菜おみくじ、ケアカレンダーなど、植物愛好家のための総合管理ツールです。
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <a
                    href="file:///C:/Users/momot/Downloads/%E3%82%B9%E3%83%9E%E3%83%9B%E3%82%A2%E3%83%97%E3%83%AA%E7%89%88.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-smartphone-line mr-2 text-xl"></i>
                    スマホ版アプリを開く
                    <i className="ri-external-link-line ml-2 text-lg"></i>
                  </a>
                  <a
                    href="file:///C:/Users/momot/Downloads/%E7%94%BB%E5%83%8F%E5%9F%8B%E3%82%81%E8%BE%BC%E3%81%BF%E5%AE%8C%E5%85%A8%E7%89%88%20(1).html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-global-line mr-2 text-xl"></i>
                    ウェブ版アプリを開く
                    <i className="ri-external-link-line ml-2 text-lg"></i>
                  </a>
                </div>

                <div className="border-t border-gray-200 pt-8">
                  <h4 className="text-xl font-bold text-green-700 mb-6 flex items-center">
                    <span className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-star-line text-green-600"></i>
                    </span>
                    主要機能（6つ）
                  </h4>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-2xl">
                          🎋
                        </div>
                        <h5 className="font-bold text-gray-800 text-lg">今日の花・野菜おみくじ</h5>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        30種類の植物から今日の運勢を占う。超吉・大吉・中吉・吉・小吉のランク付きで、花言葉とももちゃんからのアドバイス付き。
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-400 rounded-full flex items-center justify-center text-2xl">
                          🎂
                        </div>
                        <h5 className="font-bold text-gray-800 text-lg">今日の誕生花</h5>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        366日分の誕生花データベース。花のアイコン、名前、花言葉を日付に応じて自動表示。
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center text-2xl">
                          📸
                        </div>
                        <h5 className="font-bold text-gray-800 text-lg">写真で診断</h5>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        植物の写真をアップロードして診断。水やり、日当たり、温度管理などのケアアドバイスを表示。
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center text-2xl">
                          🌱
                        </div>
                        <h5 className="font-bold text-gray-800 text-lg">マイ植物</h5>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        育てている植物を登録・管理。名前と登録日時を記録し、ケアカレンダーと自動連携。
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full flex items-center justify-center text-2xl">
                          📅
                        </div>
                        <h5 className="font-bold text-gray-800 text-lg">ケアカレンダー</h5>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        植物のお世話記録を月間カレンダーで管理。水やり💧、肥料🌿、剪定✂️を記録し、植物別フィルター機能付き。
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-400 rounded-full flex items-center justify-center text-2xl">
                          📚
                        </div>
                        <h5 className="font-bold text-gray-800 text-lg">季節のケアガイド</h5>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        春夏秋冬ごとの植物ケア方法を案内。水やり、肥料、剪定、日当たり、温度管理のコツを季節別に紹介。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-8 mt-8">
                  <h4 className="text-xl font-bold text-pink-700 mb-6 flex items-center">
                    <span className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-user-smile-line text-pink-600"></i>
                    </span>
                    登場キャラクター（8種類）
                  </h4>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">🍑</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">ももちゃん</h6>
                      <p className="text-xs text-gray-600">メインキャラクター</p>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">🌹</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">バラ男の子</h6>
                      <p className="text-xs text-gray-600">ヘッダー装飾</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">🐶</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">犬キャラ</h6>
                      <p className="text-xs text-gray-600">白い蘭の花付き</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">🍃</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">葉っぱちゃん</h6>
                      <p className="text-xs text-gray-600">診断画面装飾</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">🌸</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">花ちゃん</h6>
                      <p className="text-xs text-gray-600">マイ植物装飾</p>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">👑</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">カレンダー王子</h6>
                      <p className="text-xs text-gray-600">カレンダー装飾</p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">🦊</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">キツネちゃん</h6>
                      <p className="text-xs text-gray-600">ピンクの花付き</p>
                    </div>

                    <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow duration-300">
                      <div className="text-4xl mb-2">🌳</div>
                      <h6 className="font-bold text-gray-800 text-sm mb-1">侍ツリー</h6>
                      <p className="text-xs text-gray-600">ケアガイド装飾</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                <i className="ri-information-line mr-1"></i>
                可愛いキャラクターたちと一緒に、楽しく植物を育てましょう！
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-green-800 mb-12">コンテンツ一覧</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link to="/essay" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20delicate%20writing%20journal%20with%20pressed%20flowers%20and%20botanical%20elements%2C%20soft%20pastel%20colors%2C%20elegant%20composition%20with%20vintage%20pen%20and%20dried%20flowers%2C%20artistic%20flat%20lay%20photography%2C%20simple%20clean%20background%2C%20warm%20natural%20lighting&width=400&height=300&seq=essay-flowers&orientation=landscape"
                    alt="エッセイ"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">エッセイ</h3>
                  <p className="text-gray-600">日常の体験や雑感を自由に綴る</p>
                </div>
              </div>
            </Link>

            <Link to="/today-flower" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20birthday%20flowers%20bouquet%20with%20colorful%20seasonal%20blooms%2C%20elegant%20floral%20arrangement%20with%20roses%20and%20daisies%2C%20soft%20natural%20lighting%2C%20simple%20clean%20background%2C%20celebration%20theme%20with%20calendar%20elements&width=400&height=300&seq=birthday-flowers&orientation=landscape"
                    alt="今日の誕生花"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">今日の誕生花</h3>
                  <p className="text-gray-600">誕生日や記念日の花を検索して発見</p>
                </div>
              </div>
            </Link>

            <Link to="/flower-language" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20pink%20roses%20and%20romantic%20flowers%20with%20love%20message%20card%2C%20elegant%20floral%20composition%20with%20soft%20petals%2C%20warm%20natural%20lighting%2C%20simple%20clean%20background%2C%20flower%20language%20theme%20with%20delicate%20blooms&width=400&height=300&seq=flower-language&orientation=landscape"
                    alt="花言葉"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">花言葉</h3>
                  <p className="text-gray-600">花に込められた美しい言葉の意味を探索</p>
                </div>
              </div>
            </Link>

            <Link to="/plants" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20botanical%20garden%20with%20various%20green%20plants%20and%20foliage%2C%20lush%20tropical%20leaves%20and%20ferns%20collection%2C%20natural%20lighting%2C%20simple%20clean%20background%2C%20plant%20encyclopedia%20theme%20with%20diverse%20species&width=400&height=300&seq=plants-encyclopedia&orientation=landscape"
                    alt="植物図鑑"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">植物図鑑</h3>
                  <p className="text-gray-600">様々な植物の特徴と育て方を詳しく紹介</p>
                </div>
              </div>
            </Link>

            <Link to="/prefecture-flowers" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20Japanese%20cherry%20blossoms%20and%20traditional%20regional%20flowers%2C%20elegant%20sakura%20petals%20with%20map%20of%20Japan%20elements%2C%20soft%20natural%20lighting%2C%20simple%20clean%20background%2C%20prefecture%20flowers%20theme%20with%20cultural%20motifs&width=400&height=300&seq=prefecture-flowers&orientation=landscape"
                    alt="都道府県の花"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">都道府県の花</h3>
                  <p className="text-gray-600">日本各地の県花を美しい写真と共に紹介</p>
                </div>
              </div>
            </Link>

            <Link to="/celebration-flowers" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20celebration%20flower%20bouquet%20with%20colorful%20blooms%20and%20festive%20ribbons%2C%20elegant%20floral%20arrangement%20for%20special%20occasions%2C%20warm%20natural%20lighting%2C%20simple%20clean%20background%2C%20gift%20flowers%20theme%20with%20joyful%20atmosphere&width=400&height=300&seq=celebration-flowers&orientation=landscape"
                    alt="お祝い花"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">お祝い花</h3>
                  <p className="text-gray-600">特別な日を彩るお祝い用の花をご紹介</p>
                </div>
              </div>
            </Link>

            <Link to="/flower-shop" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20flower%20shop%20interior%20with%20colorful%20fresh%20flowers%20display%2C%20elegant%20floral%20arrangements%20and%20bouquets%20for%20sale%2C%20warm%20natural%20lighting%2C%20simple%20clean%20background%2C%20shopping%20theme%20with%20vibrant%20blooms&width=400&height=300&seq=flower-shop&orientation=landscape"
                    alt="花の購入"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">花の購入</h3>
                  <p className="text-gray-600">お気に入りの花を見つけて購入</p>
                </div>
              </div>
            </Link>

            <Link to="/knowledge" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20botanical%20study%20with%20magnifying%20glass%20examining%20flower%20details%2C%20scientific%20plant%20research%20theme%20with%20vintage%20books%20and%20specimens%2C%20warm%20natural%20lighting%2C%20simple%20clean%20background%2C%20knowledge%20and%20learning%20atmosphere&width=400&height=300&seq=plant-knowledge&orientation=landscape"
                    alt="豆知識"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">豆知識</h3>
                  <p className="text-gray-600">植物の驚くべき秘密や不思議な生態を学習</p>
                </div>
              </div>
            </Link>

            <Link to="/omikuji" className="group">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Beautiful%20mystical%20flowers%20with%20fortune%20telling%20elements%2C%20magical%20blooms%20with%20soft%20glowing%20light%20and%20spiritual%20atmosphere%2C%20warm%20natural%20lighting%2C%20simple%20clean%20background%2C%20omikuji%20fortune%20theme%20with%20enchanting%20mood&width=400&height=300&seq=flower-omikuji&orientation=landscape"
                    alt="花おみくじ"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">花おみくじ</h3>
                  <p className="text-gray-600">今日のあなたにぴったりの花を占います</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
